import React, { useEffect } from "react";
import { ArrowLeft, HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "What is HEXA OS?",
    a: "HEXA OS is an AI-native web operating system that unifies your email, chat, tasks, meetings, files, documents, spreadsheets and presentations under a single identity (you@hexawebos.com). Every app has AI built into its primary workflow — not bolted on."
  },
  {
    q: "Do I need to install anything?",
    a: "No. HEXA runs entirely in your browser. You can also install it as a Progressive Web App (PWA) on desktop, iOS or Android via 'Add to Home Screen'."
  },
  {
    q: "Is there a free plan?",
    a: "Yes. The Free tier includes all apps, 5 GB cloud storage and 100 AI credits per month — no credit card required."
  },
  {
    q: "How much does the Pro plan cost?",
    a: "₹150 + 18% GST/month (≈ ₹177) for individuals. Includes 50 GB storage, 1,000 AI credits, priority support and voice features. Business plans are custom-priced — contact team@hexawebos.com."
  },
  {
    q: "Can I try HEXA before signing up?",
    a: "Yes — click 'TRY DEMO — NO SIGNUP' on hexawebos.com. You'll enter a temporary boss identity and can explore every app. Writes are gated by a signup prompt so your work is saved only after you create an account."
  },
  {
    q: "Can I use my own email address as my HEXA ID?",
    a: "Yes. HEXA supports 'Bring Your Own Email' (BYOE). Sign up with john@yourcompany.com and that becomes your HEXA identity. You then connect your real mailbox via IMAP/SMTP in Mail → External Accounts to send & receive real email."
  },
  {
    q: "Can HEXA send real emails to Gmail / Yahoo / outside recipients?",
    a: "Yes — once you connect an external mailbox (Gmail, Yahoo, Outlook or custom IMAP) with an app password, you can send from that account to any recipient on the internet. External → external routing works today."
  },
  {
    q: "Is my data safe?",
    a: "All traffic is HTTPS/TLS 1.3. Passwords are bcrypt-hashed. External mailbox credentials are encrypted at rest with Fernet symmetric encryption. Your content is never used to train third-party AI models."
  },
  {
    q: "Which AI models power HEXA?",
    a: "HEXA uses the Emergent LLM Key which covers Anthropic Claude (Sonnet 4.5), OpenAI GPT (5.2) and Google Gemini. All three are used behind the scenes for different task types."
  },
  {
    q: "What is Novo?",
    a: "Novo is HEXA's agentic AI copilot. Unlike a chatbot, Novo can plan multi-step workflows and execute them across all HEXA apps — e.g. 'Draft follow-up emails for everyone I met last week and schedule 30-min syncs with the top 3.'"
  },
  {
    q: "Can my boss see everything I do?",
    a: "Business workspaces have role-based access. Boss-only views include the Attendance dashboard, Team Day Recap, Projects workload, and workspace stats. Bosses do NOT read your personal emails, chats or documents — only usage aggregates and items you explicitly share."
  },
  {
    q: "How does the referral program work?",
    a: "Every user has a unique referral code (visible in My Profile → Referral). Share the link https://hexawebos.com/?ref=YOURCODE. When someone signs up via your link, you earn 50 coins. Coins can be redeemed against Pro / Business subscription fees."
  },
  {
    q: "Can I export my data?",
    a: "Yes. From My Profile you can export mailbox (mbox), documents (docx/xlsx/pptx/pdf), files (zip). We follow GDPR data-portability rules — you own your data."
  },
  {
    q: "How do I delete my account?",
    a: "My Profile → Advanced → Delete Account. Your data is removed from active systems within 30 days and from backups within 90 days."
  },
  {
    q: "Is HEXA available on mobile?",
    a: "Yes. HEXA is fully responsive and installable as a PWA on iOS and Android. Native iOS/Android apps are on the roadmap."
  },
  {
    q: "Who owns the content I create in HEXA?",
    a: "You do. HEXA claims no rights to your emails, documents, files or data. See our Terms & Conditions for details."
  },
  {
    q: "How do I contact HEXA?",
    a: "General: team@hexawebos.com · Privacy: privacy@hexawebos.com · Legal: legal@hexawebos.com · Security: security@hexawebos.com"
  },
];

export default function FAQPage({ onBack }) {
  useEffect(() => {
    document.title = "FAQ — HEXA OS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Frequently asked questions about HEXA OS — pricing, features, security, AI models, mobile, referrals and more.");

    // Inject FAQPage schema.org JSON-LD for rich SERP snippets & LLM citations
    let script = document.getElementById("hexa-faq-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "hexa-faq-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "name": "HEXA OS FAQ",
        "url": "https://hexawebos.com/faq",
        "inLanguage": "en",
        "isPartOf": { "@type": "WebSite", "name": "HEXA OS", "url": "https://hexawebos.com" },
        "publisher": { "@type": "Organization", "name": "HEXA", "url": "https://hexawebos.com" },
        "dateModified": "2026-02-08",
        "mainEntity": FAQS.map(({ q, a }) => ({
          "@type": "Question",
          "name": q,
          "acceptedAnswer": { "@type": "Answer", "text": a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://hexawebos.com/" },
          { "@type": "ListItem", "position": 2, "name": "FAQ",  "item": "https://hexawebos.com/faq" }
        ]
      }
    ]);
    return () => {
      const s = document.getElementById("hexa-faq-jsonld");
      if (s) s.remove();
      // Remove the canonical we set
      const c = document.querySelector('link[rel="canonical"]');
      if (c && c.getAttribute("href")?.includes("/faq")) c.remove();
    };
  }, []);

  // Set canonical on mount
  useEffect(() => {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://hexawebos.com/faq");
    document.title = "FAQ — HEXA OS";
  }, []);

  return (
    <div
      className="text-white"
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        background: "#070b1a",
        fontFamily: "'Inter', system-ui",
        WebkitOverflowScrolling: "touch",
      }}
      data-testid="page-faq"
    >
      <div className="max-w-3xl mx-auto px-6 py-14">
        <button
          data-testid="faq-back"
          onClick={onBack}
          className="mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-white flex items-center gap-1 mb-8"
        >
          <ArrowLeft size={12} /> Back to HEXA
        </button>

        <div className="flex items-center gap-3 mb-2">
          <HelpCircle size={22} className="text-[#22c55e]" />
          <h1 className="text-3xl font-bold">Frequently Asked Questions</h1>
        </div>
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-8">
          Answers about HEXA OS — pricing, security, AI, mobile & more
        </div>

        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              data-testid={`faq-item-${i}`}
              className="rounded-lg overflow-hidden"
              style={{
                background: "rgba(34,197,94,0.06)",
                border: "1px solid rgba(34,197,94,0.35)",
              }}
            >
              <div className="w-full text-left px-4 pt-3 pb-1">
                <span className="text-base font-semibold text-white/95">{faq.q}</span>
              </div>
              <div className="px-4 pb-3.5 text-[14px] leading-relaxed text-white/80">
                {faq.a}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-[13px] text-white/50 text-center">
          Still have questions? Email <a className="text-[#a78bfa] underline" href="mailto:team@hexawebos.com">team@hexawebos.com</a>
        </div>
      </div>
    </div>
  );
}
